import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AppType } from '../types';
import { exportWeeklyScheduleToPDF } from '../utils/pdfExport';
import { parseDate, toDateKey } from '../utils/dateUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const getMonday = (d: Date) => {
    const date = new Date(d);
    const day = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 5 = Friday, 6 = Saturday

    // If it's Friday (5), Saturday (6), or Sunday (0), show next week's schedule
    if (day === 5 || day === 6 || day === 0) {
        // Calculate next week's Monday
        const daysUntilNextMonday = day === 0 ? 1 : (8 - day); // Sunday = 1 day, Friday = 3 days, Saturday = 2 days
        return new Date(date.setDate(date.getDate() + daysUntilNextMonday));
    }

    // Otherwise, get this week's Monday
    const diff = date.getDate() - day + 1; // Monday is day 1
    return new Date(date.setDate(diff));
};

const PDFSchedulePage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState<string>('Initialisiere...');
    const [error, setError] = useState<string | null>(null);
    const processingRef = React.useRef(false);

    useEffect(() => {
        if (processingRef.current) return;
        processingRef.current = true;
        generatePDF().finally(() => {
            // Optional: processingRef.current = false; 
            // In StrictMode, we might not want to reset it immediately to avoid the second mount triggering it.
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const generatePDF = React.useCallback(async () => {
        try {
            setStatus('Lade Daten...');

            // Parse params
            const appParam = searchParams.get('app');
            const startDatumParam = searchParams.get('startDate');
            const logoUrlParam = searchParams.get('logo');


            let startDatum: Date;
            if (startDatumParam) {
                // parseISO reads "YYYY-MM-DD" as a local day; new Date() would read it as UTC.
                startDatum = parseDate(startDatumParam);
            } else {
                // If no date provided, default to current week Monday
                startDatum = getMonday(new Date());
            }

            const currentApp = (appParam as AppType) || AppType.LEGACY;
            const endDatum = new Date(startDatum);
            endDatum.setDate(endDatum.getDate() + 5);

            // Defaults
            let logoUrl = logoUrlParam || '/logo_praxis_hayat_seite.webp';
            let appTitle = 'Dienstplan'; // Always generic
            let filenamePrefix = '';

            // If defaults are not overridden by param, set based on AppType
            if (!logoUrlParam) {
                if (currentApp === AppType.HAUSARZT) {
                    logoUrl = '/hausarzt_logo.svg';
                    filenamePrefix = 'Hausarztmedizin';
                }
                else if (currentApp === AppType.KINDERARZT) {
                    logoUrl = '/kinder_logo.svg';
                    filenamePrefix = 'Kindermedizin';
                }
            }

            // Fetch Data from public API (no auth required)
            const response = await axios.get(`${API_BASE_URL}/public/schedule-pdf-data`, {
                params: {
                    app: currentApp,
                    startDatum: toDateKey(startDatum),
                    endDatum: toDateKey(endDatum)
                }
            });

            const { praxen, abwesenheiten, feiertage, effectiveSchedule } = response.data;

            setStatus('Generiere PDF...');

            const { doc, filename } = await exportWeeklyScheduleToPDF({
                weekStart: startDatum,
                praxen,
                zuweisungen: effectiveSchedule,
                abwesenheiten,
                feiertage,
                logoUrl,
                appTitle,
                filenamePrefix,
                includeEmptyCells: true
            });

            setStatus('Fertig! Download startet...');
            doc.save(filename);

            // Generate Blob URL
            // const blob = doc.output('blob');
            // const url = URL.createObjectURL(blob);
            // setPdfUrl(url);

            setStatus('Download erfolgreich!');

        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Ein Fehler ist aufgetreten');
            setStatus('Fehler');
        }
    }, [searchParams]);

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
            <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
                <h1 className="text-2xl font-bold mb-4 text-gray-800">PDF Export</h1>

                {error ? (
                    <div className="bg-red-50 text-red-700 p-4 rounded mb-4">
                        {error}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="text-blue-600 font-medium text-lg animate-pulse">
                            {status}
                        </div>
                        <p className="text-gray-500 text-sm">
                            Ihr Download sollte in Kürze beginnen.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PDFSchedulePage;
